import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AppSettings } from '../../app.settings';
import { TabStorageService } from '../tabstorage/tabstorage.service';
import { ApiRequest, ApiTransport } from './api-transport';

type Params = Record<string, any>;
function normalizeResourceUrl(url: string): string {
  // Restangular collapsed path separators, including those already in a custom
  // base URL. Preserve the authority, encoded IDs, and literal query/fragment data.
  const parts = /^((?:[a-z][a-z\d+.-]*:)?\/\/[^/\\?#]*)?([^?#]*)([\s\S]*)$/i.exec(url)!;
  return (parts[1] || '') + parts[2].replace(/[\\/]+/g, '/') + parts[3];
}

/** Small immutable URL builder for the resource operations used by this application.
 * Responses are ordinary JSON: no injected methods or library metadata. */
export class ApiResource {
  constructor(private transport: ApiTransport, private url: string,
              private headers: () => Params, private collection = false,
              private options: Partial<ApiRequest> = {}, private resourceId?: any) {
    this.url = normalizeResourceUrl(url);
  }
  getUrl(): string { return this.url; }
  one(route: any, id?: any): ApiResource {
    if (route === undefined || typeof route === 'number') { throw new Error('A resource route is required'); }
    return new ApiResource(this.transport, this.join(route) + (id === undefined || id === null ? '' : '/' + encodeURIComponent(String(id))), this.headers, false, {}, id);
  }
  all(route: string): ApiResource {
    return new ApiResource(this.transport, this.join(route), this.headers, true);
  }
  withHttpConfig(options: Partial<ApiRequest>): ApiResource {
    return new ApiResource(this.transport, this.url, this.headers, this.collection, {...this.options, ...options}, this.resourceId);
  }
  get(params?: Params, headers?: Params): Observable<any> { return this.request('GET', undefined, undefined, params, headers); }
  getList(pathOrParams?: string | Params, paramsOrHeaders?: Params, headers?: Params): Observable<any> {
    const path = this.collection ? undefined : typeof pathOrParams === 'string' ? pathOrParams : undefined;
    const params = typeof pathOrParams === 'object' ? pathOrParams : paramsOrHeaders;
    return this.request('GET', undefined, path, params, typeof pathOrParams === 'object' ? paramsOrHeaders : headers).pipe(map(value => {
      if (value === null) { return []; } // Existing 204 collection response contract.
      if (!Array.isArray(value)) { throw new Error('Expected an array response'); }
      return value;
    }));
  }
  remove(params?: Params, headers?: Params): Observable<any> { return this.request('DELETE', undefined, undefined, params, headers); }
  post(pathOrBody?: any, bodyOrParams?: any, paramsOrHeaders?: Params, headers?: Params): Observable<any> {
    return this.collection ? this.request('POST', pathOrBody, undefined, bodyOrParams, paramsOrHeaders)
      : this.request('POST', bodyOrParams, pathOrBody, paramsOrHeaders, headers);
  }
  patch(body?: any, params?: Params, headers?: Params): Observable<any> { return this.request('PATCH', body, undefined, params, headers); }
  put(params?: Params, headers?: Params): Observable<any> { return this.request('PUT', undefined, undefined, params, headers); }
  customGET(path?: string, params?: Params, headers?: Params): Observable<any> { return this.request('GET', undefined, path, params, headers); }
  customDELETE(path?: string, params?: Params, headers?: Params): Observable<any> { return this.request('DELETE', undefined, path, params, headers); }
  customPOST(body?: any, path?: string, params?: Params, headers?: Params): Observable<any> { return this.request('POST', body, path, params, headers); }
  customPUT(body?: any, path?: string, params?: Params, headers?: Params): Observable<any> { return this.request('PUT', body, path, params, headers); }
  customPATCH(body?: any, path?: string, params?: Params, headers?: Params): Observable<any> { return this.request('PATCH', body, path, params, headers); }
  private join(path?: string): string {
    const url = path !== undefined && path !== null && path !== ''
      ? this.url.replace(/\/$/, '') + '/' + String(path).replace(/^\//, '') : this.url;
    return normalizeResourceUrl(url);
  }
  private request(method: string, data?: any, path?: string, params?: Params, headers?: Params): Observable<any> {
    // Preserve existing bodyless resource mutations: an element sends its ID, a collection sends [].
    const body = ['GET', 'DELETE'].includes(method) ? undefined
      : data === undefined ? (this.collection ? [] : this.resourceId === undefined ? {} : {id: this.resourceId}) : data;
    return this.transport.createRequest({...this.options, method, url: this.join(path), data: body, params,
      headers: {...this.headers(), ...headers}}).pipe(map(response => response.body));
  }
}

@Injectable({providedIn: 'root'})
export class ApiClient {
  private baseUrl = AppSettings.getEndpoint();
  private defaultHeaders: Params = {};
  constructor(private transport: ApiTransport) {
    TabStorageService.credentials.subscribe(token => this.setDefaultHeaders(token ? {Authorization: `Bearer ${token}`} : {}));
  }
  setDefaultHeaders(headers: Params): void { this.defaultHeaders = {...headers}; }
  withBaseUrl(baseUrl: string): ApiClient {
    const client = new ApiClient(this.transport);
    client.baseUrl = baseUrl;
    client.setDefaultHeaders(this.defaultHeaders);
    return client;
  }
  one(route: any, id?: any): ApiResource { return this.root().one(route, id); }
  all(route: string): ApiResource { return this.root().all(route); }
  private root(): ApiResource { return new ApiResource(this.transport, this.baseUrl.replace(/\/$/, ''), () => this.defaultHeaders); }
}
